import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, Calendar, Tag, Users, Building2, Sparkles, Play,
  X, ChevronLeft, ChevronRight, ArrowRight, Quote
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, onNext, onPrev]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <button onClick={onClose} className="absolute top-6 right-6 text-white/60 hover:text-white z-10">
        <X size={32} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-6 text-white/60 hover:text-white z-10">
        <ChevronLeft size={48} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-6 text-white/60 hover:text-white z-10">
        <ChevronRight size={48} />
      </button>
      <img
        src={images[currentIndex]}
        alt=""
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="absolute bottom-6 text-white/60 text-sm">
        {currentIndex + 1} / {images.length}
      </div>
    </motion.div>
  );
};

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [relatedProjects, setRelatedProjects] = useState([]);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/public/projects/${slug}`);
        setProject(response.data);
        
        const relatedResponse = await axios.get(`${API}/public/projects?limit=4`);
        setRelatedProjects(relatedResponse.data.items?.filter(p => p.slug !== slug).slice(0, 3) || []);
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [slug]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    if (project?.gallery?.length) {
      setLightboxIndex((prev) => (prev + 1) % project.gallery.length);
    }
  };

  const prevImage = () => {
    if (project?.gallery?.length) {
      setLightboxIndex((prev) => (prev - 1 + project.gallery.length) % project.gallery.length);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-display text-4xl text-white mb-4">Proyecto no encontrado</h1>
            <Link to="/portfolio" className="btn-gold">Ver Portafolio</Link>
          </div>
        </div>
      </Layout>
    );
  }

  const galleryImages = project.gallery?.length > 0 
    ? project.gallery 
    : [project.cover_image || 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800&q=80'];

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-end" data-testid="project-hero">
        <div className="absolute inset-0">
          <img
            src={project.cover_image || 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1920&q=80'}
            alt={project.title?.[lang] || project.title?.es}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
        </div>

        <div className="container-premium relative pb-20 pt-32">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-4xl"
          >
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-3 mb-6">
              <span className="px-4 py-1.5 bg-[#D4AF37] text-black text-xs font-medium rounded-full uppercase tracking-wider">
                {project.category}
              </span>
              {project.event_type && (
                <span className="px-4 py-1.5 bg-white/10 text-white/80 text-xs rounded-full uppercase tracking-wider">
                  {project.event_type}
                </span>
              )}
            </motion.div>
            
            <motion.h1 variants={fadeInUp} className="font-display text-5xl md:text-7xl lg:text-8xl text-white mb-6">
              {project.title?.[lang] || project.title?.es}
            </motion.h1>
            
            {(project.summary?.es || project.summary?.en) && (
              <motion.p variants={fadeInUp} className="text-white/70 text-xl md:text-2xl leading-relaxed mb-8">
                {project.summary?.[lang] || project.summary?.es}
              </motion.p>
            )}
            
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-6 text-white/60">
              <span className="flex items-center gap-2">
                <MapPin size={18} className="text-[#D4AF37]" />
                {project.city}, {project.country}
              </span>
              {project.date && (
                <span className="flex items-center gap-2">
                  <Calendar size={18} className="text-[#D4AF37]" />
                  {formatDate(project.date)}
                </span>
              )}
              {project.client_type && (
                <span className="flex items-center gap-2">
                  <Building2 size={18} className="text-[#D4AF37]" />
                  {project.client_type}
                </span>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* KPIs Section */}
      {project.kpis?.length > 0 && (
        <section className="relative -mt-16 z-10 pb-8" data-testid="project-kpis">
          <div className="container-premium">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
            >
              {project.kpis.map((kpi, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="glass-dark rounded-xl p-6 text-center border border-white/5 hover:border-[#D4AF37]/30 transition-colors"
                >
                  <div className="text-[#D4AF37] text-3xl md:text-4xl font-display mb-2">
                    {kpi.value}
                  </div>
                  <div className="text-white/50 text-xs uppercase tracking-wider">
                    {kpi.label?.[lang] || kpi.label?.es || kpi.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Case Study Content */}
      <section className="section-dark py-20" data-testid="project-content">
        <div className="container-premium">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-16">
            {/* Main Content */}
            <div className="lg:col-span-3 space-y-20">
              
              {/* Full Description - Renders sections from markdown-style formatting */}
              {(project.description?.es || project.description?.en) && (
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  className="space-y-12"
                >
                  <h2 className="font-display text-3xl text-white mb-8">
                    {lang === 'es' ? 'El Proyecto' : 'The Project'}
                  </h2>
                  <div className="prose prose-lg prose-invert max-w-none">
                    {(project.description?.[lang] || project.description?.es)?.split('\n\n').map((block, idx) => {
                      // Check if block is a header (starts with **)
                      if (block.startsWith('**') && block.includes('**')) {
                        const headerMatch = block.match(/^\*\*([^*]+)\*\*/);
                        if (headerMatch) {
                          const header = headerMatch[1];
                          const content = block.replace(/^\*\*[^*]+\*\*\s*\n?/, '');
                          return (
                            <div key={idx} className="mb-8">
                              <h3 className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 font-medium">
                                {header}
                              </h3>
                              <p className="text-white/70 leading-relaxed text-lg whitespace-pre-line">
                                {content}
                              </p>
                            </div>
                          );
                        }
                      }
                      // Regular paragraph
                      return (
                        <p key={idx} className="text-white/70 leading-relaxed text-lg whitespace-pre-line mb-6">
                          {block}
                        </p>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Photo Gallery */}
              {galleryImages.length > 0 && (
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <h2 className="font-display text-3xl text-white mb-8">
                    {lang === 'es' ? 'Galería del Proyecto' : 'Project Gallery'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {galleryImages.map((image, index) => (
                      <div
                        key={index}
                        className={`overflow-hidden cursor-pointer group ${index === 0 ? 'md:col-span-2' : ''}`}
                        onClick={() => openLightbox(index)}
                      >
                        <img
                          src={image}
                          alt={`${project.title?.[lang]} - ${index + 1}`}
                          className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${index === 0 ? 'aspect-[21/9]' : 'aspect-video'}`}
                        />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Videos */}
              {project.videos?.length > 0 && (
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <h2 className="font-display text-3xl text-white mb-8">
                    {lang === 'es' ? 'Videos del Evento' : 'Event Videos'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {project.videos.map((video, index) => (
                      <a
                        key={index}
                        href={video}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative aspect-video bg-black/50 rounded-xl overflow-hidden group"
                      >
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Play size={32} className="text-black ml-1" fill="black" />
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Results / Highlights */}
              {(project.results?.es || project.results?.en) && (
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <h2 className="font-display text-3xl text-white mb-8">
                    {lang === 'es' ? 'Resultados' : 'Results'}
                  </h2>
                  <div className="glass-dark rounded-2xl p-8">
                    <Sparkles className="text-[#D4AF37] mb-4" size={32} />
                    <p className="text-white/80 text-lg leading-relaxed">
                      {project.results?.[lang] || project.results?.es}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Testimonial */}
              {(project.testimonial?.es || project.testimonial?.en) && (
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                >
                  <div className="relative">
                    <Quote size={64} className="text-[#D4AF37]/20 absolute -top-4 -left-4" />
                    <blockquote className="glass-dark rounded-2xl p-10 pl-14">
                      <p className="text-white text-xl md:text-2xl italic leading-relaxed mb-6">
                        "{project.testimonial?.[lang] || project.testimonial?.es}"
                      </p>
                    </blockquote>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Project Details */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="glass-dark rounded-2xl p-8 sticky top-32"
              >
                <h4 className="text-white font-medium mb-6">
                  {lang === 'es' ? 'Detalles del Proyecto' : 'Project Details'}
                </h4>
                
                <ul className="space-y-5">
                  <li>
                    <span className="text-white/40 text-xs uppercase tracking-wider block mb-1">
                      {lang === 'es' ? 'Categoría' : 'Category'}
                    </span>
                    <span className="text-white">{project.category}</span>
                  </li>
                  
                  {project.event_type && (
                    <li>
                      <span className="text-white/40 text-xs uppercase tracking-wider block mb-1">
                        {lang === 'es' ? 'Tipo de Evento' : 'Event Type'}
                      </span>
                      <span className="text-white">{project.event_type}</span>
                    </li>
                  )}
                  
                  <li>
                    <span className="text-white/40 text-xs uppercase tracking-wider block mb-1">
                      {lang === 'es' ? 'Ubicación' : 'Location'}
                    </span>
                    <span className="text-white">{project.city}, {project.country}</span>
                  </li>
                  
                  {project.date && (
                    <li>
                      <span className="text-white/40 text-xs uppercase tracking-wider block mb-1">
                        {lang === 'es' ? 'Fecha' : 'Date'}
                      </span>
                      <span className="text-white">{formatDate(project.date)}</span>
                    </li>
                  )}
                  
                  {project.client_type && (
                    <li>
                      <span className="text-white/40 text-xs uppercase tracking-wider block mb-1">
                        {lang === 'es' ? 'Cliente' : 'Client'}
                      </span>
                      <span className="text-white">{project.client_type}</span>
                    </li>
                  )}
                </ul>

                {/* Services Involved */}
                {project.services_involved?.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <span className="text-white/40 text-xs uppercase tracking-wider block mb-3">
                      {lang === 'es' ? 'Servicios Involucrados' : 'Services Involved'}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {project.services_involved.map((service, index) => (
                        <span key={index} className="px-3 py-1 bg-white/5 text-white/70 text-xs rounded-full">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {project.tags?.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <span className="text-white/40 text-xs uppercase tracking-wider block mb-3">Tags</span>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, index) => (
                        <span key={index} className="flex items-center gap-1 px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded-full">
                          <Tag size={10} />
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA */}
                <div className="mt-8 pt-6 border-t border-white/10">
                  <p className="text-white/60 text-sm mb-4">
                    {lang === 'es' 
                      ? '¿Quieres un proyecto similar?' 
                      : 'Want a similar project?'
                    }
                  </p>
                  <Link to="/contact" className="btn-gold w-full text-center block" data-testid="project-cta">
                    {lang === 'es' ? 'Empezar Proyecto' : 'Start Project'}
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="section-subtle py-20" data-testid="related-projects">
          <div className="container-premium">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.div variants={fadeInUp} className="flex items-end justify-between mb-12">
                <h2 className="font-display text-3xl text-white">
                  {lang === 'es' ? 'Más Proyectos' : 'More Projects'}
                </h2>
                <Link to="/portfolio" className="text-white/60 hover:text-white flex items-center gap-2">
                  {t('portfolio.view_all')}
                  <ArrowRight size={16} />
                </Link>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedProjects.map((relatedProject) => (
                  <motion.div key={relatedProject.slug} variants={fadeInUp}>
                    <Link to={`/portfolio/${relatedProject.slug}`} className="block group">
                      <div className="relative aspect-video overflow-hidden mb-4">
                        <img
                          src={relatedProject.cover_image || 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&q=80'}
                          alt={relatedProject.title?.[lang] || relatedProject.title?.es}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
                        <div className="absolute bottom-0 left-0 right-0 p-5">
                          <p className="text-[#D4AF37] text-xs tracking-wider uppercase mb-1">
                            {relatedProject.category}
                          </p>
                          <h3 className="font-display text-xl text-white">
                            {relatedProject.title?.[lang] || relatedProject.title?.es}
                          </h3>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <Lightbox
            images={galleryImages}
            currentIndex={lightboxIndex}
            onClose={() => setLightboxOpen(false)}
            onNext={nextImage}
            onPrev={prevImage}
          />
        )}
      </AnimatePresence>
    </Layout>
  );
}
